import classNames from 'classnames'
import { FC } from 'react'
import { useDrop } from 'react-dnd'
import { FightEvent, FightEventFlag, SpellAssignment } from 'web/data/models'
import { uniqueIdFromSpellAssignment } from 'web/engine/spell-engine'
import { DndType, isSpellAssignmentDndItem } from 'web/utils/dnd-utils'
import { secondsToTimestamp } from 'web/utils/time-utils'
import { isSetupFightEvent } from 'web/utils/type-utils'
import { assertDefined, assumeDefined, conditionalCallback, joinLines } from 'web/utils/utils'
import { DivBtn } from '../generic/DivBtn'
import { WithTooltip } from '../generic/WithTooltip'
import { EventComment } from '../shared/EventComment'
import { EventFlags } from '../shared/EventFlags'
import { DeleteEventButton } from './DeleteEventButton'
import { EventAssignmentPayload } from './EventAssignment'
import { FightEventAssignments } from './FightEventAssignments'

export type FightEventItemProps = {

    /**
     * The event underlying the component.
     */
    fightEvent: FightEvent

    /**
     * Defines a list of interactive props that can be used to change the
     * appearance of the component.
     */
    interactive?: {
        extraClassNames: (eventId: string) => string | undefined
    }

    /**
     * If the item should list assignments, pass them here.
     */
    assignments?: {
        eventAssignments: (eventId: string) => EventAssignmentPayload[]
        onAdd?: (spellAssignment: SpellAssignment) => void
    }

    /**
     * Indicates that we're in outline editing mode.
     */
    outlineEditMode?: {
        onEdit: (eventId: string) => void
        onDelete: (eventId: string, skipConfirm: boolean) => void
    }

    /**
     * Indicates that we're editing the outline from the setup mode.
     */
    setupEditMode?: {
        onEditEvent: (eventId: string, segmentId: string) => void
    }

}

export const FightEventItem: FC<FightEventItemProps> = (props) => {

    if (props.assignments?.onAdd) {
        return (
            <InnerDndFightEventItem {...props} />
        )
    }

    return <InnerFightEventItem {...props} />

}

/**
 * Default event item, no drop support.
 */
const InnerFightEventItem: FC<FightEventItemProps & { dropRef?: any }> = (props) => {

    const { fightEvent, outlineEditMode, interactive, assignments } = props
    const timestamp = secondsToTimestamp(fightEvent.offset)
    const eventAssignments = assignments?.eventAssignments(fightEvent.id) ?? []

    const eventIsSetupFightEvent = isSetupFightEvent(fightEvent)
    const canBeEdited = outlineEditMode != null && fightEvent.userCreated

    return (
        <div
            ref={props.dropRef}
            className={classNames(
                "d-flex justify-content-between",
                interactive?.extraClassNames(fightEvent.id),
            )}
        >
            {eventIsSetupFightEvent && (
                <WithTooltip place='top' centerText>
                    {(ttId) => (
                        <div
                            style={{
                                background: '#3d3d3d30',
                                height: 'auto',
                                width: '5px',
                                // Shift the timer back in
                                marginRight: '-5px',
                                // Ensure we can still access this
                                // element even though the timer is
                                // covering it
                                zIndex: 2,
                            }}
                            data-for={ttId}
                            data-tip={joinLines([
                                `This event is not part of the fight outline`,
                                `and is only visible for the currently`,
                                `selected setup.`
                            ])}
                        />
                    )}
                </WithTooltip>
            )}

            <DivBtn
                fillWidth
                clickThrough={!canBeEdited}
                onClick={() => {
                    if (canBeEdited) {
                        outlineEditMode.onEdit(fightEvent.id)
                    }
                }}
            >
                <div className="p-2 pl-3 d-flex space-mx-3">
                    <span>{timestamp}</span>

                    <div className="d-flex align-items-center">
                        <div className="d-flex">
                            <EventNameElement
                                flagStyle={fightEvent.flags.includes(FightEventFlag.Emphasize)
                                    ? 'empahsize'
                                    : fightEvent.flags.includes(FightEventFlag.Muted)
                                        ? 'muted'
                                        : undefined
                                }
                                eventName={fightEvent.name}
                                onClick={conditionalCallback(props.setupEditMode != null, () => {
                                    assumeDefined(props, 'setupEditMode').setupEditMode
                                        .onEditEvent(fightEvent.id, fightEvent.fightSegmentId)
                                })}
                            />

                            {fightEvent.comment &&
                                <EventComment
                                    comment={fightEvent.comment}
                                    className="cursor-default"
                                />
                            }
                        </div>

                        {fightEvent.flags.length >= 1 &&
                            <EventFlags flags={fightEvent.flags} />
                        }
                    </div>
                </div>
            </DivBtn >

            {assignments &&
                <FightEventAssignments
                    fightEvent={fightEvent}
                    eventAssignments={eventAssignments}
                />
            }

            {canBeEdited &&
                <DeleteEventButton
                    onClick={(skipConfirm) => {
                        assertDefined(outlineEditMode)
                        outlineEditMode.onDelete(fightEvent.id, skipConfirm)
                    }}
                />
            }
        </div >
    )

}

const EventNameElement: FC<{
    flagStyle?: "empahsize" | "muted"
    eventName: string
    onClick: (() => void) | undefined
}> = (props) => {

    const innerElement = (
        <div
            className={classNames(
                {"font-weight-bold": props.flagStyle === 'empahsize'},
                {"small text-muted": props.flagStyle === 'muted'},
                { "font-italic text-muted": props.eventName.length <= 0 }
            )}
        >
            {props.eventName.length <= 0 ? "Untitled" : props.eventName}
        </div>
    )

    if (props.onClick) {
        return (
            <div className="cursor-pointer" onClick={props.onClick} tabIndex={0}>
                {innerElement}
            </div>
        )
    } else {
        return innerElement
    }
}

/**
 * Event item with drop-support.
 */
const InnerDndFightEventItem: FC<FightEventItemProps> = (props) => {

    const { fightEvent, assignments } = props
    const eventAssignments = assignments?.eventAssignments(fightEvent.id) ?? []

    const [, dropRef] = useDrop({
        accept: [DndType.SpellAssignment, DndType.TeamAssignment],
        collect: (monitor) => {
            return {
                highlighted: monitor.canDrop(),
                hovered: monitor.isOver(),
            }
        },
        canDrop: (item) => {
            // The only time we're not allowed to drop is if this exact event
            // already has this spell assigned to it.
            if (isSpellAssignmentDndItem(item)) {
                const { id } = fightEvent
                const { spellId, playerId } = item.payload

                const assignmentId = uniqueIdFromSpellAssignment({
                    fightEventId: id,
                    playerId,
                    spellId
                })

                const assignmentAlreadyAssigned = eventAssignments
                    .findIndex(sa => sa.assignmentState.uniqueId === assignmentId) >= 0

                if (assignmentAlreadyAssigned) {
                    return false
                }

                return true
            }

            return true
        },
        drop: (item) => {
            if (isSpellAssignmentDndItem(item)) {
                const { playerId, spellId, eventOffset } = item.payload

                assignments?.onAdd?.({
                    playerId,
                    spellId,
                    eventOffset,
                    fightEventId: fightEvent.id,
                })
            }
        }
    })

    return <InnerFightEventItem {...props} dropRef={dropRef} />
}
